import * as Yup from 'yup';
import dayjs from 'dayjs';
import { TFunction } from 'i18next';
import { AgeSource } from '@/api/enum/common.enum';
import { notFutureDate } from './dateHelper';

export const ESTIMATED_AGE_MIN = 0;
export const ESTIMATED_AGE_MAX = 120;

/** Age as entered on a form: the radio button plus the value of each source. */
export interface AgeFormValues {
	ageSource?: AgeSource | string | null;
	dateOfBirth?: string | Date | null;
	estimatedAge?: number | string | null;
}

/** What the API takes: only the chosen source has a value. */
export interface AgePayload {
	ageSource: AgeSource;
	dateOfBirth: string | null;
	estimatedAge: number | null;
}

export const isEstimatedAgeSource = (ageSource?: AgeSource | string | null) =>
	ageSource !== null &&
	ageSource !== undefined &&
	ageSource !== '' &&
	Number(ageSource) === AgeSource.estimatedAge;

/**
 * Displays an age with the estimate marker: `19 (Estimated)` on English pages,
 * `19(估算)` on Chinese ones. Blank when there is no age.
 */
export function formatAge(
	age?: number | string | null,
	estimated?: boolean | null,
	lang: 'en' | 'zh' = 'en'
): string {
	if (age === null || age === undefined || age === '') return '';
	if (!estimated) return `${age}`;
	return lang === 'zh' ? `${age}(估算)` : `${age} (Estimated)`;
}

/**
 * Age on `date` from form values, `null` without an age. The entered estimate is
 * this year's age, so another year adds or takes the difference (CHA-010 §4).
 */
export function ageOnDate(
	values: AgeFormValues | null | undefined,
	date: dayjs.ConfigType
): number | null {
	const on = dayjs(date);
	if (isEstimatedAgeSource(values?.ageSource)) {
		const estimate = values?.estimatedAge;
		if (estimate === null || estimate === undefined || estimate === '')
			return null;
		if (!/^\d+$/.test(`${estimate}`)) return null;
		return Number(estimate) + (on.year() - dayjs().year());
	}
	if (!values?.dateOfBirth) return null;
	return on.diff(dayjs(values.dateOfBirth), 'year');
}

/** Form values from an API record. Without an age source the member uses Date of birth. */
export function toAgeFormValues(data?: AgeFormValues | null) {
	const estimated = isEstimatedAgeSource(data?.ageSource);
	return {
		ageSource: estimated ? AgeSource.estimatedAge : AgeSource.dateOfBirth,
		dateOfBirth: estimated ? null : (data?.dateOfBirth ?? null),
		estimatedAge: estimated ? (data?.estimatedAge ?? '') : '',
	};
}

/** Request body fields: the value of the other source is cleared. */
export function toAgePayload(values?: AgeFormValues | null): AgePayload {
	const estimated = isEstimatedAgeSource(values?.ageSource);
	const estimatedAge = values?.estimatedAge;
	return {
		ageSource: estimated ? AgeSource.estimatedAge : AgeSource.dateOfBirth,
		dateOfBirth: estimated ? null : (values?.dateOfBirth as string) || null,
		estimatedAge:
			estimated && estimatedAge !== '' && estimatedAge != null
				? Number(estimatedAge)
				: null,
	};
}

interface AgeSchemaOptions {
	/** Date of birth is required when it is the chosen source. */
	dateOfBirthRequired?: boolean;
	/** The estimate is required when it is the chosen source (P.A. may leave it empty). */
	estimatedAgeRequired?: boolean;
	/**
	 * The estimate the record already has. It passes even above 120, which the
	 * yearly increase can reach, so that other fields can still be saved.
	 */
	getOriginalEstimatedAge?: () => number | string | null | undefined;
	/** Validate the age only while another field matches, e.g. a status. */
	activeWhen?: [field: string, is: (value: any) => boolean];
}

/** Yup fields for `ageSource`, `dateOfBirth` and `estimatedAge`. */
export function ageSchemaFields(
	t: TFunction,
	{
		dateOfBirthRequired = true,
		estimatedAgeRequired = true,
		getOriginalEstimatedAge,
		activeWhen,
	}: AgeSchemaOptions = {}
) {
	const keys = activeWhen ? ['ageSource', activeWhen[0]] : ['ageSource'];
	const isActive = (other: unknown) => !activeWhen || activeWhen[1](other);

	return {
		ageSource: Yup.mixed().nullable(),
		dateOfBirth: Yup.string()
			.nullable()
			.when(keys, ([ageSource, other], schema) => {
				if (!isActive(other) || isEstimatedAgeSource(ageSource)) return schema;
				return (
					dateOfBirthRequired
						? schema.required(t('errorMessage.pleaseSelect'))
						: schema
				).test(notFutureDate(t('errorMessage.dateCannotBeFuture')));
			}),
		estimatedAge: Yup.string()
			.nullable()
			.when(keys, ([ageSource, other], schema) => {
				if (!isActive(other) || !isEstimatedAgeSource(ageSource)) return schema;
				return (
					estimatedAgeRequired
						? schema.required(t('errorMessage.pleaseEnter'))
						: schema
				).test({
					name: 'estimatedAgeRange',
					message: t('errorMessage.estimatedAgeRange'),
					test: (value?: string | null) => {
						if (!value) return true;
						if (!/^\d+$/.test(`${value}`)) return false;
						const age = Number(value);
						const original = getOriginalEstimatedAge?.();
						if (
							original !== null &&
							original !== undefined &&
							original !== '' &&
							age === Number(original)
						)
							return true;
						return age >= ESTIMATED_AGE_MIN && age <= ESTIMATED_AGE_MAX;
					},
				});
			}),
	};
}
